import React from 'react';
import Browser from '@wetransfer/concorde-browser';

import UploadInProgressWarning from '../info/upload-in-progress-warning';

import Actions from '@app/store/action';
import Store from '@app/store';

import I18n from '@module/i18n';
import Analytics from '@module/analytics';
import Router from '@module/router';
import Button from '@component/form/element/button';
import Checkbox from '@component/form/element/checkbox';
import TextField from '@component/form/element/textfield';

import LaunchDarkly from '@module/launchdarkly';
import { EXPERIMENT_SIGN_IN_PANEL_REDESIGN } from '@constant/experiments';

export default class SignInForm extends React.Component {
  /**
   * Binds all private methods
   * @param  {Object} props Component props
   */
  constructor(props) {
    super(props);
    this.state = {
      email: '',
      password: '',
      remember_me: true,
      showUploadInProgressWarning: false,
    };

    this.detectAutoFill = this.detectAutoFill.bind(this);
    this.signinAttempt = this.signinAttempt.bind(this);
  }

  componentDidMount() {
    this.detectAutoFill();
  }

  componentWillUnmount() {
    Store.dispatch(Actions.Notification.resetNotification());
  }

  resetFormErrors() {
    Store.dispatch(Actions.Errors.resetFormErrors('signin'));
  }

  detectAutoFill() {
    const email = this.emailNode.value || '';
    const password = this.passwordNode.value || '';
    this.setState({ email: email.trim(), password });
  }

  async signinAttempt() {
    try {
      await Store.dispatch(
        Actions.User.signin({
          email: this.state.email,
          password: this.state.password,
          rememberMe: this.state.remember_me,
        })
      );

      Analytics.trackSnowplowEvent({
        category: 'plus',
        action: 'click_signin',
        label: 'half_panel',
      });

      document.activeElement.blur();
    } catch (error) {
      if (error.message === 'RequireOTPVerification') {
        Router.navigateTo('/two-factor-authentication', false, false, {
          context: {
            email: this.state.email,
            password: this.state.password,
            rememberMe: this.state.remember_me,
          },
        });
      }
    }
  }

  handleFormSubmit = (event) => {
    const { upload } = Store.getState();
    event.preventDefault();
    if (upload.isPending) {
      this.setState({ showUploadInProgressWarning: true });
      return;
    }
    this.signinAttempt();
  };

  continueUpload = () => {
    this.setState({ showUploadInProgressWarning: false });
  };

  render() {
    const { pending, errors } = Store.getState();
    const valid = errors.signin ? -1 : 0;
    const buttonActive =
      !errors.signin &&
      this.state.email &&
      this.state.password &&
      !pending.signin;
    const forgotPasswordLink = (
      <a
        key="forgot_password_link"
        href="/forgot-password"
        tabIndex="-1"
        className="forgot-password-link"
      >
        {' '}
        {I18n.t('form.signin.link.forgot_password')}{' '}
      </a>
    );

    const signinButton = (
      <Button
        active={!!buttonActive}
        text={I18n.t('form.login.button.submit')}
        tabIndex={4}
        submit={true}
      />
    );

    const signinOptions = (
      <div className="signin-form__options-wrapper">
        <Checkbox
          checked={this.state.remember_me}
          className="signin-form__options"
          label={I18n.t('form.signin.label.remember_me')}
          name="remember_me"
          onChange={(e) => this.setState({ remember_me: e.target.checked })}
          tabIndex={3}
          size={Browser.isMobile ? 'medium' : 'small'}
        />
        {forgotPasswordLink}
      </div>
    );

    const renderSigninFormActions = () => {
      if (LaunchDarkly.getVariant(EXPERIMENT_SIGN_IN_PANEL_REDESIGN)) {
        return (
          <React.Fragment>
            {signinOptions}
            {signinButton}
          </React.Fragment>
        );
      }
      return (
        <React.Fragment>
          {signinButton}
          {signinOptions}
        </React.Fragment>
      );
    };

    return (
      <div className="signin-form">
        <div className="signin-form--form-container">
          <h2>{I18n.t('form.signin.title')}</h2>
          <form
            method="post"
            ref={(node) => (this.signinFormNode = node)}
            onSubmit={this.handleFormSubmit}
            onKeyDown={this.resetFormErrors}
          >
            <TextField
              value={this.state.email}
              valid={valid}
              fieldRef={(node) => (this.emailNode = node)}
              onChange={(e) => this.setState({ email: e.target.value })}
              onBlur={this.detectAutoFill}
              onFocus={this.detectAutoFill}
              tabIndex={1}
              type="email"
              name="email"
              label={I18n.t('form.signin.label.email')}
            />
            <TextField
              value={this.state.password}
              valid={valid}
              fieldRef={(node) => (this.passwordNode = node)}
              onChange={(e) => this.setState({ password: e.target.value })}
              onBlur={this.detectAutoFill}
              onFocus={this.detectAutoFill}
              tabIndex={2}
              type="password"
              name="password"
              label={I18n.t('form.signin.label.password')}
            />

            {renderSigninFormActions()}
          </form>
        </div>
        {this.state.showUploadInProgressWarning && (
          <UploadInProgressWarning
            cancelUpload={this.signinAttempt}
            continueUpload={this.continueUpload}
          />
        )}
      </div>
    );
  }
}
